<template>
  <div class="coming-soon">
    <Sidebar />
    
    <!-- Top Actions Bar -->
    <div class="top-bar">
      <div class="search-bar">
        <i class="search-icon">🔍</i>
        <input 
          type="text" 
          v-model="searchQuery" 
          placeholder="Search by title or description..."
          class="search-input"
        >
      </div>
      <button @click="showAddModal = true" class="add-button">
        <i class="icon">+</i> Add New Coming Soon
      </button>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>

    <div v-else-if="error" class="error">
      <i class="error-icon">⚠️</i>
      <p>{{ error }}</p>
    </div>

    <div v-else class="coming-soon-grid">
      <div v-if="filteredItems.length === 0" class="no-results">
        <i class="no-results-icon">🔍</i>
        <p>No items found matching your search.</p>
      </div>
      <div v-for="item in filteredItems" :key="item.id" class="coming-soon-card">
        <div class="card-actions">
          <button @click="editItem(item)" class="action-button edit">
            <i class="edit-icon">✏️</i>
          </button>
          <button @click="deleteItem(item.id)" class="action-button delete">
            <i class="delete-icon">🗑️</i>
          </button>
        </div>
        <div class="card-image-container">
          <img :src="item.image_url" :alt="item.title" class="card-image">
        </div>
        <div class="card-content">
          <h3>{{ item.title }}</h3>
          <p>{{ item.description }}</p>
          <div v-if="item.expected_release_date" class="release-date">
            <i class="calendar-icon">📅</i>
            Expected Release: {{ formatDate(item.expected_release_date) }}
          </div>
        </div>
      </div>
    </div>

    <!-- Add Modal -->
    <div v-if="showAddModal" class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Add New Coming Soon</h2>
          <button @click="showAddModal = false" class="close-button">×</button>
        </div>
        <form @submit.prevent="createItem" class="modal-form">
          <div class="form-group">
            <label>Title</label>
            <input v-model="newItem.title" required>
          </div>
          <div class="form-group">
            <label>Title (Arabic)</label>
            <input v-model="newItem.title_ar" required>
          </div>
          <div class="form-group">
            <label>Description</label>
            <QuillRichTextEditor
              v-model="newItem.description"
              placeholder="Enter description"
              :minHeight="'150px'"
              required
            />
          </div>
          <div class="form-group">
            <label>Description (Arabic)</label>
            <QuillRichTextEditor
              v-model="newItem.description_ar"
              placeholder="أدخل الوصف"
              :toolbar="arabicToolbar"
              :minHeight="'150px'"
              required
            />
          </div>
          <div class="form-group">
            <label>Image URL</label>
            <input v-model="newItem.image_url" required>
          </div>
          <div class="form-group">
            <label>Image URL (Arabic)</label>
            <input v-model="newItem.image_url_ar" required>
          </div>
          <div class="form-group">
            <label>Expected Release Date</label>
            <input type="date" v-model="newItem.expected_release_date">
          </div>
          <div class="form-actions">
            <button type="button" @click="showAddModal = false" class="cancel-button">Cancel</button>
            <button type="submit" class="submit-button">Create</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit Modal -->
    <div v-if="showEditModal" class="modal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Edit Coming Soon</h2>
          <button @click="showEditModal = false" class="close-button">×</button>
        </div>
        <form @submit.prevent="updateItem" class="modal-form">
          <div class="form-group">
            <label>Title</label>
            <input v-model="editingItem.title" required>
          </div>
          <div class="form-group">
            <label>Title (Arabic)</label>
            <input v-model="editingItem.title_ar" required>
          </div>
          <div class="form-group">
            <label>Description</label>
            <QuillRichTextEditor
              v-model="editingItem.description"
              placeholder="Enter description"
              :minHeight="'150px'"
              required
            />
          </div>
          <div class="form-group">
            <label>Description (Arabic)</label>
            <QuillRichTextEditor
              v-model="editingItem.description_ar"
              placeholder="أدخل الوصف"
              :toolbar="arabicToolbar"
              :minHeight="'150px'"
              required
            />
          </div>
          <div class="form-group">
            <label>Image URL</label>
            <input v-model="editingItem.image_url" required>
          </div>
          <div class="form-group">
            <label>Image URL (Arabic)</label>
            <input v-model="editingItem.image_url_ar" required>
          </div>
          <div class="form-group">
            <label>Expected Release Date</label>
            <input type="date" v-model="editingItem.expected_release_date">
          </div>
          <div class="form-actions">
            <button type="button" @click="showEditModal = false" class="cancel-button">Cancel</button>
            <button type="submit" class="submit-button">Update</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import axios from 'axios';
import Sidebar from '@/components/Sidebar.vue';
import QuillRichTextEditor from '@/components/QuillEditor.vue';

const comingSoonItems = ref([]);
const loading = ref(true);
const error = ref(null);
const showAddModal = ref(false);
const showEditModal = ref(false);
const editingItem = ref(null);
const newItem = ref({
  title: '',
  title_ar: '',
  description: '',
  description_ar: '',
  image_url: '',
  image_url_ar: '',
  expected_release_date: null,
  is_active: true
});

const searchQuery = ref('');

const API_URL = 'https://api.kewaar.com/api/admin/coming-soons';
const API_TOKEN = '4967|S0XijZfUTkcDKkyE4Ytb2AGhdhe1YBShjDdfj7Ek16f5802d';

const headers = {
  'Authorization': `Bearer ${API_TOKEN}`
};

const arabicToolbar = ref([
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ 'header': 1 }, { 'header': 2 }],
  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
  [{ 'script': 'sub' }, { 'script': 'super' }],
  [{ 'indent': '-1' }, { 'indent': '+1' }],
  [{ 'direction': 'rtl' }],
  [{ 'size': ['small', false, 'large', 'huge'] }],
  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
  [{ 'color': [] }, { 'background': [] }],
  [{ 'font': [] }],
  [{ 'align': ['', 'right', 'center', 'justify'] }],
  ['clean'],
  ['link', 'image', 'video']
]);

const fetchComingSoonItems = async () => {
  try {
    const response = await axios.get(API_URL, { headers });
    comingSoonItems.value = response.data.data;
  } catch (err) {
    error.value = 'Failed to load coming soon items. Please try again later.';
    console.error('Error fetching coming soon items:', err);
  } finally {
    loading.value = false;
  }
};

const createItem = async () => {
  try {
    loading.value = true;
    await axios.post(API_URL, newItem.value, { headers });
    await fetchComingSoonItems();
    showAddModal.value = false;
    newItem.value = {
      title: '',
      title_ar: '',
      description: '',
      description_ar: '',
      image_url: '',
      image_url_ar: '',
      expected_release_date: null,
      is_active: true
    };
  } catch (err) {
    error.value = 'Failed to create item. Please try again.';
    console.error('Error creating item:', err);
  } finally {
    loading.value = false;
  }
};

const editItem = (item) => {
  editingItem.value = { ...item };
  showEditModal.value = true;
};

const updateItem = async () => {
  try {
    loading.value = true;
    await axios.put(`${API_URL}/${editingItem.value.id}`, editingItem.value, { headers });
    await fetchComingSoonItems();
    showEditModal.value = false;
    editingItem.value = null;
  } catch (err) {
    error.value = 'Failed to update item. Please try again.';
    console.error('Error updating item:', err);
  } finally {
    loading.value = false;
  }
};

const deleteItem = async (id) => {
  if (!confirm('Are you sure you want to delete this item?')) return;
  
  try {
    loading.value = true;
    await axios.delete(`${API_URL}/${id}`, { headers });
    await fetchComingSoonItems();
  } catch (err) {
    error.value = 'Failed to delete item. Please try again.';
    console.error('Error deleting item:', err);
  } finally {
    loading.value = false;
  }
};

const formatDate = (date) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
};

// Add computed property for filtered items
const filteredItems = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return comingSoonItems.value;
  
  return comingSoonItems.value.filter(item => {
    const title = item.title?.toLowerCase() || '';
    const titleAr = item.title_ar?.toLowerCase() || '';
    const description = item.description?.toLowerCase() || '';
    const descriptionAr = item.description_ar?.toLowerCase() || '';
    
    return title.includes(query) || 
           titleAr.includes(query) || 
           description.includes(query) || 
           descriptionAr.includes(query);
  });
});

onMounted(() => {
  fetchComingSoonItems();
});
</script>

<style scoped>
.coming-soon {
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
}

.top-bar {
  position: fixed;
  top: 1.5rem;
  right: 2rem;
  left: calc(250px + 2rem);
  z-index: 100;
  display: flex;
  gap: 1.5rem;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.search-bar {
  flex: 1;
  max-width: 500px;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  background: #f8f9fa;
}

.search-input:focus {
  outline: none;
  border-color: #4CAF50;
  background: white;
  box-shadow: 0 0 0 3px rgba(74, 175, 80, 0.1);
}

.add-button {
  background: #4CAF50;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(74, 175, 80, 0.2);
}

.add-button:hover {
  background: #45a049;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(74, 175, 80, 0.2);
}

.coming-soon-grid {
  flex: 1;
  padding: 2rem;
  margin-left: 250px;
  padding-top: 6rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  align-items: start;
}

.coming-soon-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  height: fit-content;
  display: flex;
  flex-direction: column;
  position: relative;
}

.coming-soon-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.card-image-container {
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  overflow: hidden;
}

.card-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.coming-soon-card:hover .card-image {
  transform: scale(1.05);
}

.card-content {
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.card-content h3 {
  margin: 0;
  font-size: 1.25rem;
  color: #2c3e50;
  font-weight: 600;
}

.card-content p {
  margin: 0;
  color: #6c757d;
  line-height: 1.6;
  flex: 1;
}

.release-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

.card-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 2;
}

.coming-soon-card:hover .card-actions {
  opacity: 1;
}

.action-button {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-button:hover {
  transform: scale(1.1);
}

.action-button.edit {
  color: #2196F3;
}

.action-button.delete {
  color: #f44336;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #2c3e50;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s;
}

.close-button:hover {
  color: #2c3e50;
}

.modal-form {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  background: #f8f9fa;
}

.form-group input:focus {
  outline: none;
  border-color: #4CAF50;
  background: white;
  box-shadow: 0 0 0 3px rgba(74, 175, 80, 0.1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.cancel-button {
  padding: 0.75rem 1.5rem;
  border: 1px solid #e9ecef;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  color: #6c757d;
  transition: all 0.2s;
}

.cancel-button:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.submit-button {
  padding: 0.75rem 1.5rem;
  background: #4CAF50;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.submit-button:hover {
  background: #45a049;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(74, 175, 80, 0.2);
}

.loading, .error {
  flex: 1;
  margin-left: 250px;
  padding-top: 6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4CAF50;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.error {
  text-align: center;
  color: #dc3545;
  padding: 2rem;
}

.error-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.no-results {
  grid-column: 1 / -1;
  text-align: center;
  padding: 3rem;
  color: #6c757d;
  font-size: 1.1rem;
}

.no-results-icon {
  font-size: 2rem;
  margin-bottom: 1rem;
  opacity: 0.5;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* RTL Support */
:dir(rtl) .coming-soon-card {
  text-align: right;
}

:dir(rtl) .search-icon {
  left: auto;
  right: 1rem;
}

:dir(rtl) .search-input {
  padding: 0.75rem 2.5rem 0.75rem 1rem;
}

:dir(rtl) .card-actions {
  right: auto;
  left: 1rem;
}
</style> 